import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { analyticsLogger } from 'commons/src/analytics';
import { CUSTOM_THRESHOLD_SAVED } from 'commons/src/analytics/AnalyticsEvents';
import { Option } from 'commons/src/components/dropdown/MultipleAttrDropdown';
import { getSelectedGroupFromStorage } from 'commons/src/components/findUserType';
import { ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ErrorType, SelectedPeriod } from 'commons/src/models/commonTypeScript';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import { fetchSpaces, fetchSpaceSensorData } from '../../../../actions/spaceActions';
import { ThresholdConfigSensor, updateThresholdsApi, UpdateThresholdsRequest } from '../../../../api/thresholdsApi';

export type UseSpaceThresholdModalProps = {
    thresholds: SpaceThresholds;
    locationId: string;
    spaceId: string;
    selectedPeriod: SelectedPeriod;
    sensorType: string;
    onClose: () => void;
};
type SelectedThresholdScope = { id: string; scope: ThresholdsResourceScope; inputValue: string } | null;

export type UseSpaceThresholdModalReturn = {
    scopeModified: boolean;
    thresholdScopeOptions: Option[];
    selectedValues: number[];
    setSelectedValues: (value: number[]) => void;
    loading: boolean;
    error: ErrorType | undefined;
    handleThresholdScopeChange: (args: { id: string; inputValue: string }) => void;
    selectedThresholdScope: SelectedThresholdScope | null;
    onSave: (sensorValues: ThresholdConfigSensor) => void;
    getButtonText: () => string;
};

const useSpaceThresholdModal = ({
    thresholds,
    locationId,
    spaceId,
    selectedPeriod,
    onClose,
}: UseSpaceThresholdModalProps): UseSpaceThresholdModalReturn => {
    const dispatch = useDispatch();
    const { t: txt } = useTranslation();
    const thresholdScopeOptions: Option[] = [
        { id: ThresholdsResourceScope.USER_GROUP, inputValue: txt('CustomThresholdScope.AccountDefault') },
        { id: ThresholdsResourceScope.LOCATION, inputValue: txt('CustomThresholdScope.CustomBuilding') },
        { id: ThresholdsResourceScope.SPACE, inputValue: txt('CustomThresholdScope.CustomSpace') },
    ];
    const userGroupId = getSelectedGroupFromStorage()?.userGroupId ?? '';
    const idMapping: Record<ThresholdsResourceScope, string> = {
        [ThresholdsResourceScope.USER_GROUP]: userGroupId,
        [ThresholdsResourceScope.LOCATION]: locationId,
        [ThresholdsResourceScope.SPACE]: spaceId,
    };
    const activeScope = thresholds?.thresholdOptions?.activeScope || ThresholdsResourceScope.USER_GROUP;
    const [scopeModified, setScopeModified] = useState(false);
    const [selectedValues, setSelectedValues] = useState<number[]>([]);
    const [selectedThresholdScope, setSelectedThresholdScope] = useState<SelectedThresholdScope>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<ErrorType | undefined>(undefined);

    useEffect(() => {
        const defaultScope = activeScope || ThresholdsResourceScope.USER_GROUP;
        const defaultOption = thresholdScopeOptions?.find(option => option.id === defaultScope);
        if (defaultOption) {
            setSelectedThresholdScope({
                id: idMapping[defaultScope],
                scope: defaultScope,
                inputValue: defaultOption.inputValue,
            });
        } else {
            setSelectedThresholdScope(null);
        }
    }, [activeScope]);

    const prevLoadingRef: React.MutableRefObject<boolean> = useRef(loading);
    useEffect(() => {
        if (prevLoadingRef.current && !loading && !error) {
            onClose();
        }
        prevLoadingRef.current = loading;
    }, [loading, prevLoadingRef]);

    const updateThresholds = useCallback(async (request: UpdateThresholdsRequest): Promise<void> => {
        setLoading(true);
        await updateThresholdsApi(request)
            .then(() => {
                setLoading(false);
                dispatch(fetchSpaces(locationId));
                dispatch(fetchSpaceSensorData(locationId, spaceId, selectedPeriod));
            })
            .catch(err => {
                setLoading(false);
                setError(err);
            });
    }, []);

    const logThresholdSaved = (request: UpdateThresholdsRequest): void => {
        analyticsLogger(CUSTOM_THRESHOLD_SAVED, {
            resourceScope: request.resourceScope,
            id: request.id,
            sensors: request.sensors.map(sensor => ({
                sensor: sensor.sensor,
                thresholds: sensor.thresholds.map(threshold => ({
                    thresholdType: threshold.thresholdType,
                    value: threshold.value,
                })),
            })),
        });
    };

    const onSave = (sensorValues: ThresholdConfigSensor): void => {
        if (!selectedThresholdScope?.id) {
            return;
        }
        const request: UpdateThresholdsRequest = {
            resourceScope: selectedThresholdScope?.scope as ThresholdsResourceScope,
            id: selectedThresholdScope?.id,
            sensors: [sensorValues],
        };
        logThresholdSaved(request);
        updateThresholds(request).catch(() => setScopeModified(false));
    };
    const getButtonText = (): string => {
        const customScope = selectedThresholdScope?.scope || ThresholdsResourceScope.USER_GROUP;
        return txt(`CustomThresholds.ThresholdsText${customScope}`);
    };
    const handleThresholdScopeChange = ({ id, inputValue }: { id: string; inputValue: string }): void => {
        const updatedId = idMapping[id as ThresholdsResourceScope];
        setSelectedThresholdScope({ id: updatedId, scope: id as ThresholdsResourceScope, inputValue });
        setScopeModified(true);
    };

    return {
        scopeModified,
        thresholdScopeOptions,
        selectedValues,
        setSelectedValues,
        loading,
        error,
        handleThresholdScopeChange,
        selectedThresholdScope,
        onSave,
        getButtonText,
    };
};

export default useSpaceThresholdModal;
